import type { SerializeFrom } from '~/types/serialize-from';
import React, { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import {
    Outlet,
    isRouteErrorResponse,
    useLocation,
    useMatches,
    useNavigation,
    useRouteError,
    useFetcher,
    useNavigate,
} from 'react-router';
import { useLatest, useDebounceFn } from 'ahooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { CatchPage, ServerErrorPage } from '@modules/error';
import { useLogger } from '@archipro-website/logger/client';
import {
    makePageViewTrackingData,
    useLogPageView,
    useTracker,
    useLogLinkClicks,
} from '@archipro-website/tracker';
import { tileSliderAtom } from '@rocco/states/tileSliderAtom';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { CartPopupProvider } from '@modules/ecommerce/context/CartPopupContext';
import { useHideNavigation } from '@modules/root/hook/use-hide-navigation';
import { useWarningMessages } from '@modules/root/hook/use-warning-messages';
import {
    useAppData,
    useHeaderHandle,
    useMatchesHandleData,
    useMaybeAppData,
    useRootData,
    getServerVersion,
    useFeature,
} from '@modules/root';
import { useLeadModal } from '~/modules/b2b/lead-form/useLeadModal';
import { isB2BRootLoaderData } from '~/utils/business-subdomain';
import { HomeFooter } from '@ui-rocco/pages/home-page/sections/HomeFooter';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import { useOneTapSignIn } from '~/modules/user/hook/use-one-tap-sign-in';
import CountrySuggestModal from '@modules/root/component/country-suggest-modal/CountrySuggestModal';
import SiteWideModals from '@modules/root/component/site-wide-modals/SiteWideModals';
import { ToasterProvider, useToaster } from '../context/ToasterContext';
import { FollowSelectorProvider } from '~/modules/moodboards/components';
import { useOnObjectChange } from '@archipro-website/react-hooks';
import { useDeploymentShouldReload } from '@archipro-website/react-hooks';
import { Flex, ImageConfigProvider, useStyles } from '@archipro-design/aria';
import * as S from '../component/app-page-layout/AppPageLayout.style';
import { imageConfig } from '~/modules/images';
import { useAuthEvent } from '../hook/use-auth-event';
import { AuthContextProvider } from '~/modules/root/context';
import type { loader } from '~/routes/_app';
import type { GrowthBookSSRData } from '@growthbook/growthbook-react';
import { useGrowthBookSSR } from '@growthbook/growthbook-react';
import type { HeaderCartInfo } from '@rocco/ui/website-header/components/Header';
import { Header } from '@rocco/ui/website-header/components/Header';
import { AdvertTopBanner } from '@modules/adverts/component/advert-top-banner/AdvertTopBanner';
import { useTopBannerOffset } from '@modules/adverts/hook/use-top-banner-offset';
import {
    DESKTOP_NAV,
    MOBILE_HEADER_PRIMARY_NAV,
    MOBILE_HEADER_SECONDARY_NAV,
} from '~/utils/route-config';
import { placeCountryDomain } from '~/utils/urlHelper';
import { getDomain } from '~/utils/get-domain';
import {
    countriesList,
    type SupportedCountry,
} from '@archipro-website/localisation';
import { getUserHeaderData } from '../util/get-user-header-data';
import { useLogout } from '~/modules/user/hook/use-logout';
import {
    pathsThatShouldRedirectToHome,
    buildCountryLink,
    stripLocationFromCountrySwitchPath,
} from '@archipro-website/top-navigation/src/lib/utils/country-link';
import {
    usePageTheme,
    useHeaderSolidTheme,
} from '~/modules-rocco/pages/hooks/use-page-theme';
// import { useAnimatedFavicon } from '@rocco/ui/favicon/hook/use-animated-favicon';
import { Toaster as RoccoToaster } from '@rocco/components/sonner';
import { SocialShareInitializer } from '~/modules/social-sharing/components/SocialShareInitializer';
import { MobileSearchModal } from '~/modules-rocco/search/page/mobile-search-modal-flow';
import { ClientOnly } from 'remix-utils';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useEnquiryAutoSubmit } from '~/modules/enquiry/hooks/use-enquiry-auto-submit';
import { uiStateAtomAddProfessionalModalOpened } from '~/modules-rocco/root/states/ui-state-add-professional-modal-opened';
import { footerElementAtom } from '~/state/project-detail-states';
import { uiStateAtomSearchModalOpened } from '~/modules-rocco/search/states/ui-state-search-modal-opened';
import {
    addSavedPinKeys,
    makePinKey,
    savedPinKeysAtom,
} from '~/modules/moodboards/state/design-board-save-state';
import { DownloadGateModal } from '~/modules-rocco/product/component/DownloadGateModal';
import { EnquiryModal } from '~/modules/enquiry/component/EnquiryModal';
import { cn } from '@rocco/utils/cn';
import type { HeaderCountryElement } from '~/utils/country-switch';
import { getCountrySwitchUrl } from '~/utils/country-switch';
import { getCloneCountryStateAtom } from '@archipro-website/web/app/state/ui-state-clone-country-link';
import { useUtmCampaignProfessional } from '~/modules/tracking/hook/useAdProfessional';
import { getPermissions, useBatchLoadUserActiveStatuses } from '~/modules/user';
import {
    useNotifications,
    useNotificationTitleBadge,
} from '~/modules/notifications';
import { PermissionEnum } from 'generated/graphql';
import {
    useChatBoxes,
    useChatGlobals,
    useContactSuggestionsCache,
    useConversations,
    useTypingIndicatorManager,
} from '~/modules/chat';
import { ChatBoxes } from '~/modules/chat/components/ChatBoxes';
import { useSalesMode } from '../hook/use-sales-mode';
import { useSearch } from '~/modules/search/hook/use-search';
import type { SearchSuggestionResultGroup } from '@rocco/ui/search/view-models/search-suggestion';
import { useSearchTracking } from '@ui-rocco/hooks/use-search-tracking';
import { removeHDEFromNavLinks } from './hde-country-gating';
import { useInlineWarningBanner } from '~/ui-rocco/pages/details-page/revamp/hooks/use-inline-warning-banner';
import { WarningBanner as RoccoWarningBanner } from '@rocco/ui/warning-banner/components/WarningBanner';

const AppProviders = ({ children }: { children: React.ReactNode }) => {
    return (
        <ToasterProvider>
            <CartPopupProvider>
                <FollowSelectorProvider>{children}</FollowSelectorProvider>
            </CartPopupProvider>
        </ToasterProvider>
    );
};

export type AppData = SerializeFrom<typeof loader>;

export const links = () => [
    {
        rel: 'stylesheet',
        href: '/assets/website/styles/swiper@9.1.0/swiper-bundle.min.css',
    },
];

/**
 * renders top nav and footer around its children
 * It requires app loader to return data.
 * @param children
 * @param data
 * @constructor
 */
const AppLayout = ({
    children,
    data,
}: {
    children: JSX.Element;
    data: AppData;
}) => {
    const config = useConfigContext();
    const location = useLocation();
    const matches = useMatches();
    const warningMessages = useWarningMessages();
    const setFooterElement = useSetAtom(footerElementAtom);
    const footerRef = useRef<HTMLDivElement>(null);

    // useAnimatedFavicon();

    /**
     * we use useMatches here because we cannot call useLoaderData inside
     * Error boundaries. Since we are can't guarantee that we do have app data
     * we use the optional version
     */
    const {
        shoppingCartSummary,
        user,
        toastMessage,
        moodBoardPageIds,
        webSocketUrl,
        topBannerAd,
    } = data;

    const isLoggedIn = user.__typename === 'Me';
    const showTextLogoOnly =
        location.pathname === '/' || location.pathname === '';

    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const buildHubEnabled = useFeature('buildhub');
    const eventTicketingEnabled = useFeature('event_ticketing');
    const newChatUI2025Enabled = useFeature('new_chat_ui_2025');
    const getMatchedEnabled = useFeature('get_matched_homepage');
    const getMatchedRequestsOnlineEnabled = useFeature(
        'get_matched_requests_online',
    );
    const disableTileSliderProduct = useFeature('disable_tile_slider_product');
    const disableTileSliderProject = useFeature('disable_tile_slider_project');
    const productsPageTopnavDec2025Enabled = useFeature(
        'products_page_topnav_dec_2025',
    );
    const topBannerEnabled = useFeature('top_ad_banner');
    // Gate listing→detail view transitions (BC-4786). On `_app` (not `root`)
    // so print routes never call useFeature. Dev ?transitions=1 latches in
    // sessionStorage across navigations and full reloads.
    const viewTransitionsFlag = useFeature('details_transition_2026');
    const viewTransitionsDevLatch = useRef(false);
    if (import.meta.env.DEV && typeof window !== 'undefined') {
        if (new URLSearchParams(location.search).get('transitions') === '1') {
            viewTransitionsDevLatch.current = true;
            window.sessionStorage.setItem('ap-transitions-dev', '1');
        } else if (
            window.sessionStorage.getItem('ap-transitions-dev') === '1'
        ) {
            viewTransitionsDevLatch.current = true;
        }
    }
    const viewTransitionsEnabled =
        viewTransitionsFlag || viewTransitionsDevLatch.current;
    const setViewTransitionsEnabled = useSetAtom(
        uiContextAtomViewTransitionsEnabled,
    );
    setViewTransitionsEnabled(viewTransitionsEnabled);
    useEffect(() => {
        document.documentElement.toggleAttribute(
            'data-view-transitions',
            viewTransitionsEnabled,
        );
        return () => {
            document.documentElement.removeAttribute('data-view-transitions');
        };
    }, [viewTransitionsEnabled]);
    const showTopBanner = topBannerEnabled && !!topBannerAd;
    const contentClassName = useMatchesHandleData<string>(
        'contentClassName',
        '',
    );
    const headerHandle = useHeaderHandle();

    const {
        env,
        config: rootConfig,
        growthbookContext,
        user: trackedUser,
    } = useRootData();
    const deployNotificationEnabled =
        rootConfig.deployNotification?.enabled ?? false;

    const logger = useLogger();
    const tracker = useTracker();
    const { onLogoutClick } = useLogout();
    const { notifications } = useNotifications({ global: true });
    const { isSearching } = useSearch();
    const setTileSlider = useSetAtom(tileSliderAtom);
    const { isChatBoxesDisabled } = useChatBoxes();
    useChatGlobals({ global: true });
    useConversations({ global: true, isDisabled: !newChatUI2025Enabled });
    useNotificationTitleBadge(!newChatUI2025Enabled);
    useContactSuggestionsCache(!newChatUI2025Enabled);
    useTypingIndicatorManager();
    useGrowthBookSSR(growthbookContext as GrowthBookSSRData);
    useOneTapSignIn(config.google.otsId, isBMPreview);
    useBatchLoadUserActiveStatuses();

    // Fires page view events
    useLogPageView(location, tracker, (state) =>
        makePageViewTrackingData(state),
    );

    // Remember the professional whose ad brought the visitor here, so their
    // conversions can be credited to that client.
    useUtmCampaignProfessional();

    // Fire website referral & download tracking events
    useLogLinkClicks(location, tracker, logger, trackedUser);

    useEnquiryAutoSubmit();

    useAuthEvent(tracker);

    useOnObjectChange(
        {
            hashKey: 'feature-flags',
            value: {
                user: {
                    id: user.ID,
                    type: user.__typename,
                },
                features: user.FeatureFlags.map(({ Name, Enabled }) => {
                    return { Name, Enabled };
                }),
            },
            callback: () => {
                window.location.reload();
            },
        },
        [user],
    );

    const { topNavigation, footerNavigation } = useHideNavigation(matches);
    // The unified header is now the default site-wide. Routes can
    // optionally provide a `header` handle for per-page tagline / CTAs /
    // colour variant. When the handle is present we also bypass
    // `hideTopNavigation` so the v2 header renders on routes that previously
    // opted out via the legacy hide handle.
    //
    // Exception: the b2b-by-host pages (served at root on business subdomains)
    // render their own host-resolved `B2BTopNav` inside their hero, so keep the
    // unified header off there to avoid a double nav. Detected via the b2b root
    // loader payload on the matched routes.
    const isB2BPage = matches.some((match) => isB2BRootLoaderData(match.data));
    const isStandardHeader = !isB2BPage;
    const renderTopNavigation =
        topNavigation || (isStandardHeader && !!headerHandle);
    const { openModal: openLeadModal } = useLeadModal();
    const headerPrimaryCta = headerHandle?.primaryCta;
    const handleHeaderPrimaryCtaClick =
        isStandardHeader && headerPrimaryCta?.opensModal
            ? () => openLeadModal({ ctaLabel: headerPrimaryCta.label })
            : undefined;

    // Pages that render their own full-page chrome (e.g. the B2B landing pages
    // served at the consumer root paths on the business subdomain) flag
    // `hideChrome` from their loader. They share a route file with a consumer
    // page, so a static route `handle` can't be used. Honour the flag here to
    // also drop the mobile content top-padding and suppress the floating chat.
    const hideChrome = matches.some(
        (match) =>
            (match.data as { hideChrome?: boolean } | null)?.hideChrome ===
            true,
    );

    const { toast } = useToaster();

    useEffect(() => {
        if (!toastMessage) return;
        toast(toastMessage.message, {
            type: toastMessage.type,
        });
    }, [toastMessage, toast]);

    const shouldReload = useDeploymentShouldReload({
        clientVersion: env.version,
        host: env.host,
        getServerVersion,
        webSocketUrl,
        enabled: deployNotificationEnabled,
    });
    const navigation = useNavigation();
    const shouldReloadRef = useLatest(shouldReload);

    const countryData = useAtomValue(getCloneCountryStateAtom);
    const inlineWarningBanner = useInlineWarningBanner();

    useEffect(() => {
        if (
            navigation.state === 'loading' &&
            navigation.location &&
            shouldReloadRef.current()
        ) {
            window.location.replace(
                `${navigation.location.pathname}${navigation.location.search}`,
            );
        }
    }, [navigation, shouldReloadRef]);

    const { appDisplayMode, localisation } = useRootData();
    const isMobile = appDisplayMode === 'mobile';
    const currentCountryCode = localisation.country; // FIXME
    const { countryConfig } = localisation;
    const domain = getDomain(currentCountryCode);
    const isChatBoxEnabled =
        newChatUI2025Enabled &&
        !isChatBoxesDisabled &&
        isLoggedIn &&
        !isMobile &&
        !isBMPreview &&
        !hideChrome;

    const pathParts = location.pathname.split('/').filter(Boolean) ?? [];
    const shouldRedirectHome = pathsThatShouldRedirectToHome.includes(
        pathParts[0] ?? '',
    );

    const pathAndSearch = shouldRedirectHome
        ? ''
        : stripLocationFromCountrySwitchPath(
              (location.pathname ?? '') + (location.search ?? ''),
          );

    // All supported markets (NZ, AU, US) are now publicly live, so every market
    // shows the full country switcher list — no per-site filtering.
    const countries = Object.values(countriesList).map((country) => {
        const targetCountryCode = country.shortName as SupportedCountry;

        const countryElement: HeaderCountryElement = {
            code: country.shortName,
            name: country.name,
            url:
                buildCountryLink(env, currentCountryCode, targetCountryCode) +
                pathAndSearch,
        };
        return {
            ...countryElement,
            url: getCountrySwitchUrl(
                countryElement,
                countryData,
                location.pathname,
            ),
        };
    });

    const isGetMatchedAdmin =
        user.__typename === 'Me' && Boolean(user.CanAccessGetMatchedAdmin);

    const { headerUserData, profileLinks } = getUserHeaderData(
        user,
        notifications,
        buildHubEnabled,
        currentCountryCode,
        eventTicketingEnabled,
        newChatUI2025Enabled,
        {
            allowMemberGuides: countryConfig.allowMemberGuides,
            allowMemberOrders: countryConfig.allowMemberOrders,
            allowMemberTickets: countryConfig.allowMemberTickets,
        },
        isGetMatchedAdmin,
        getMatchedEnabled,
        getMatchedRequestsOnlineEnabled,
    );
    const currentUrl = location.pathname + location.search;

    const pageTheme = usePageTheme();
    // Loader-driven solid header theme (e.g. the project revamp's dark bar),
    // falling back to the route handle's static `solidTheme`.
    const loaderSolidTheme = useHeaderSolidTheme();
    const [currentPageTheme, setCurrentPageTheme] = useState(pageTheme);
    // Routes that pin a transparent-overlay header own their own scroll
    // transition (the MobileHeader switches itself to solid past the hero), so
    // the scroll-driven flip to `default` here only adds the mobile nav-clearance
    // padding mid-scroll — a layout shift that fights slow scrolling on the
    // full-bleed hero. Skip it for those routes and keep the page theme stable.
    const ownsHeroTransition = headerHandle?.headerVariant === 'transparent';
    useEffect(() => {
        setCurrentPageTheme(pageTheme);
        if (!isMobile || pageTheme === 'none' || ownsHeroTransition) return;
        const toggleTheme = () => {
            if (window.scrollY > 100) {
                setCurrentPageTheme('default');
            } else {
                setCurrentPageTheme(pageTheme);
            }
        };
        toggleTheme();
        window.addEventListener('scroll', toggleTheme);
        return () => {
            window.removeEventListener('scroll', toggleTheme);
        };
    }, [currentUrl, pageTheme, isMobile, ownsHeroTransition]);

    const { css } = useStyles({ currentPageTheme });
    const [, setSearchModalOpened] = useAtom(uiStateAtomSearchModalOpened);

    const { setLoginModal } = useSetLoginModal();
    const setAddProfessionalModalOpened = useSetAtom(
        uiStateAtomAddProfessionalModalOpened,
    );

    const setSavedPinKeys = useSetAtom(savedPinKeysAtom);
    const hasInitialisedSavedPins = useRef(false);

    useEffect(() => {
        if (hasInitialisedSavedPins.current) {
            return;
        }

        hasInitialisedSavedPins.current = true;

        if (!moodBoardPageIds) {
            setSavedPinKeys(new Set());
            return;
        }

        const pageKeys = moodBoardPageIds.pageIDs.map((id) =>
            makePinKey(id, 'page'),
        );
        const imageKeys = moodBoardPageIds.imageIDs.map((id) =>
            makePinKey(id, 'image'),
        );

        setSavedPinKeys(
            addSavedPinKeys(new Set(), [...pageKeys, ...imageKeys]),
        );
    }, [moodBoardPageIds, setSavedPinKeys]);

    const canApproveContent = getPermissions(
        user,
        PermissionEnum.BmContentApprove,
    );
    const canCreateProfessional = getPermissions(
        user,
        PermissionEnum.BmProfessionalCreate,
    );

    const { canSeeSalesData, isSalesMode, toggleSalesMode } = useSalesMode(
        isLoggedIn ? user : null,
    );

    const additionalUserMenuItems =
        canCreateProfessional || canApproveContent
            ? {
                  links: [
                      ...(canCreateProfessional
                          ? [
                                {
                                    title: 'Add Professional',
                                    to: '#',
                                    onClick: () => {
                                        setAddProfessionalModalOpened(true);
                                    },
                                },
                            ]
                          : []),
                      ...(canApproveContent
                          ? [
                                {
                                    title: 'Content Hub',
                                    to: '/ap-admin/content/projects',
                                    external: true,
                                },
                            ]
                          : []),
                      ...(canSeeSalesData
                          ? [
                                {
                                    title: isSalesMode
                                        ? 'Exit Sales Admin'
                                        : 'Enter Sales Admin',
                                    to: '#',
                                    onClick: () => {
                                        toast(
                                            isSalesMode
                                                ? 'Sales mode disabled.'
                                                : 'Welcome to Sales Admin.',
                                            {
                                                type: 'success',
                                            },
                                        );
                                        toggleSalesMode();
                                    },
                                },
                            ]
                          : []),
                  ],
              }
            : undefined;

    const onMobileSearchClick = () => {
        setSearchModalOpened(true);
    };

    const primaryDesktopLinks = DESKTOP_NAV;
    const countryAwareDesktopLinks = removeHDEFromNavLinks(
        primaryDesktopLinks,
        countryConfig.showHDENavLink,
    );
    const countryAwareSecondaryMobileLinks = removeHDEFromNavLinks(
        MOBILE_HEADER_SECONDARY_NAV,
        countryConfig.showHDENavLink,
    );
    const shoppingCart = shoppingCartSummary?.ShoppingCart;
    const cartInfo: HeaderCartInfo = {
        items:
            shoppingCart?.LastAddedItems?.map((el) => ({
                id: el.ID,
                title: el.Variant?.Product?.Title,
                price: el.Price.ShortFormat,
                quantity: el.Quantity,
                isSample: el.Sample,
                thumbnail: {
                    desktop: el.Variant?.Thumbnail?.Desktop ?? '',
                    mobile: el.Variant?.Thumbnail?.Mobile ?? '',
                },
            })) || [],
        itemTotal: {
            ShortFormat: shoppingCart?.ItemTotal?.ShortFormat || '',
            Amount: shoppingCart?.ItemTotal?.Amount || 0,
        },
        cartCount: notifications?.cart?.count ?? 0,
    };

    const onNavigate = useNavigate();

    // Set footer element for background height calculation
    useEffect(() => {
        if (footerRef.current) {
            setFooterElement(footerRef.current);
        }
        return () => {
            setFooterElement(null);
        };
    }, [setFooterElement]);

    // Header search tracking configuration
    const headerSearchTrackingConfig = useSearchTracking();

    // Header search suggestions fetcher
    const headerSearchSuggestionsFetcher =
        useFetcher<SearchSuggestionResultGroup | null>();

    // Fetch suggestions when user types in header search (debounced)
    const { run: fetchHeaderSearchSuggestions } = useDebounceFn(
        (query: string) => {
            const params = new URLSearchParams({ search: query.trim() });
            headerSearchSuggestionsFetcher.load(
                `/actions/search/search-suggestions?${params.toString()}`,
            );
        },
        { wait: 50 }, // keep it as low as possible to avoid feel lag
    );

    const isHighAdmin =
        trackedUser?.__typename === 'Me' &&
        'IsHighAdmin' in trackedUser &&
        trackedUser.IsHighAdmin === true;

    useEffect(() => {
        // Enable/disable tile sliders based on the website settings.
        // BC-4449: the `tile-slider-disabled` A/B gate has been removed. The
        // product slider is controlled solely by its CMS feature flag, while the
        // project slider is additionally restricted to ArchiPro high admins.
        setTileSlider((state) => ({
            ...state,
            enableTileSliderProduct: !disableTileSliderProduct,
            enableTileSliderProject: !disableTileSliderProject && isHighAdmin,
        }));
    }, [
        disableTileSliderProduct,
        disableTileSliderProject,
        isHighAdmin,
        setTileSlider,
    ]);

    // avoid duplicate scrollbar in iframe in BM profile preview
    const isIframe = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return window.self !== window.top;
    }, []);
    const pageRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (isIframe && pageRef.current) {
            pageRef.current.style.overflow = location.pathname.startsWith(
                '/professional-preview/',
            )
                ? 'visible'
                : '';
        }
    }, [isIframe, pageRef, location.pathname]);

    // Site-wide top ad banner: measure its height and drive the CSS vars the
    // header reads so the fixed/absolute header is pushed below it.
    const bannerRef = useRef<HTMLDivElement>(null);
    useTopBannerOffset(bannerRef, pageRef, showTopBanner, topBannerAd?.id);

    return (
        <ImageConfigProvider value={imageConfig}>
            <AuthContextProvider>
                <Flex
                    ref={pageRef}
                    id="app-page-layout"
                    column
                    className={cn(css(S.PageBase))}
                >
                    {/* First tab stop on every page: visually hidden until
                        keyboard-focused, then overlays the top-left corner. */}
                    <a
                        href="#app-page-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[10000] focus:bg-white focus:px-4 focus:py-3 focus:font-sans focus:text-base focus:text-[#181818] focus:shadow-md"
                    >
                        Skip to main content
                    </a>
                    {showTopBanner && topBannerAd ? (
                        <>
                            {/* SSR defaults for the header offset vars so the
                                fixed/absolute header sits below the banner on
                                first paint, before the client hook measures the
                                exact height. Matches the banner band's reserved
                                min-heights (3.75rem mobile / 7.5rem desktop); the
                                hook then overrides via inline style. Scoped so
                                banner-less pages keep the 0px fallback. */}
                            <style
                                dangerouslySetInnerHTML={{
                                    __html:
                                        '#app-page-layout{--ad-top-banner-height:100px;--ad-top-banner-offset:100px}' +
                                        '@media(min-width:768px){#app-page-layout{--ad-top-banner-height:231px;--ad-top-banner-offset:231px}}',
                                }}
                            />
                            <div ref={bannerRef} className="relative w-full">
                                <AdvertTopBanner ad={topBannerAd} />
                            </div>
                        </>
                    ) : null}
                    {/* BC-4705: the product detail revamp gets the warning
                        banner ABOVE the top nav so it never overlaps the nav's
                        logomark or the hero. `useInlineWarningBanner` derives
                        this from route matches, so server and client render the
                        banner in the same place (no post-hydration jump).
                        Desktop only — the mobile header is fixed, so it renders
                        the banner itself at the top of its strip. */}
                    {inlineWarningBanner &&
                    !isMobile &&
                    warningMessages?.length ? (
                        <RoccoWarningBanner alerts={warningMessages} />
                    ) : null}
                    {renderTopNavigation ? (
                        <Header
                            showTextLogoOnly={showTextLogoOnly}
                            cartInfo={cartInfo}
                            onUserCartClick={() => {
                                window.location.href = '/shopping-cart';
                            }}
                            currentUrl={currentUrl}
                            userData={headerUserData}
                            currentCountryCode={currentCountryCode}
                            allowMemberGuides={countryConfig.allowMemberGuides}
                            allowMemberOrders={countryConfig.allowMemberOrders}
                            onNavigate={onNavigate}
                            countries={countries}
                            primaryDesktopLinks={countryAwareDesktopLinks}
                            profileMobileLinks={profileLinks}
                            primaryMobileLinks={placeCountryDomain(
                                MOBILE_HEADER_PRIMARY_NAV,
                                domain,
                            )}
                            secondaryMobileLinks={placeCountryDomain(
                                countryAwareSecondaryMobileLinks,
                                domain,
                            )}
                            onLogout={onLogoutClick}
                            displayMode={appDisplayMode}
                            color={
                                headerHandle?.headerVariant ??
                                (currentPageTheme === 'transparent'
                                    ? 'transparent'
                                    : currentPageTheme === 'app'
                                      ? 'app'
                                      : currentPageTheme === 'none'
                                        ? 'none'
                                        : 'solid')
                            }
                            solidTheme={
                                loaderSolidTheme ?? headerHandle?.solidTheme
                            }
                            mode={'fixed'}
                            additionalUserMenuItems={additionalUserMenuItems}
                            isSearching={isSearching}
                            warningMessages={warningMessages}
                            onLoginClick={() => {
                                setLoginModal({
                                    authSource: 'topNav',
                                    isOpen: true,
                                });
                            }}
                            onMobileSearchClick={onMobileSearchClick}
                            fetchSuggestions={fetchHeaderSearchSuggestions}
                            suggestionData={headerSearchSuggestionsFetcher.data}
                            headerSearchTrackingConfig={
                                headerSearchTrackingConfig
                            }
                            productsPageTopnavDec2025Enabled={
                                productsPageTopnavDec2025Enabled
                            }
                            tagline={
                                isStandardHeader
                                    ? headerHandle?.tagline
                                    : undefined
                            }
                            primaryCta={
                                isStandardHeader
                                    ? headerHandle?.primaryCta
                                    : undefined
                            }
                            secondaryCta={
                                isStandardHeader
                                    ? headerHandle?.secondaryCta
                                    : undefined
                            }
                            onPrimaryCtaClick={handleHeaderPrimaryCtaClick}
                            showLogomark={
                                isStandardHeader
                                    ? headerHandle?.showLogomark
                                    : undefined
                            }
                            secondaryNavLinks={
                                isStandardHeader &&
                                headerHandle?.secondaryNavLinks
                                    ? {
                                          links: headerHandle.secondaryNavLinks,
                                      }
                                    : undefined
                            }
                            suppressWarningBanner={inlineWarningBanner}
                        />
                    ) : null}
                    <div
                        className={cn(
                            css(S.PageContent),
                            location.pathname.includes('/project/')
                                ? 'bg-gray-50'
                                : '',
                            'app-page-content',
                            contentClassName,
                            // Strip the mobile top padding that clears the
                            // global nav, which is hidden on hide-chrome pages.
                            hideChrome && 'pt-0!',
                        )}
                        id="app-page-content"
                    >
                        {children}
                    </div>
                    {footerNavigation ? (
                        <div ref={footerRef}>
                            {/* New site-wide footer (BC-4466), replacing the legacy footer.
                                Self-contained: sources country/HDE gating from
                                useLocalisation. Defaults to the dark theme. */}
                            <HomeFooter />
                        </div>
                    ) : null}
                </Flex>
                <CountrySuggestModal />
                <SiteWideModals />
                <RoccoToaster />
                <SocialShareInitializer displayMode={appDisplayMode} />
                <EnquiryModal />
                <DownloadGateModal />
                {isMobile && <MobileSearchModal />}
                {isChatBoxEnabled && (
                    <ClientOnly>{() => <ChatBoxes user={user} />}</ClientOnly>
                )}
            </AuthContextProvider>
        </ImageConfigProvider>
    );
};

export const ErrorBoundary = () => {
    const data = useMaybeAppData();
    const error = useRouteError();

    if (isRouteErrorResponse(error)) {
        if (data) {
            return (
                <AppProviders>
                    <AppLayout data={data}>
                        <CatchPage caught={error} />
                    </AppLayout>
                </AppProviders>
            );
        }
        return (
            <AppProviders>
                <CatchPage caught={error} />
            </AppProviders>
        );
    }

    let errorObj = new Error('Unknown error');
    if (error instanceof Error) {
        errorObj = error;
    }

    return (
        <AppProviders>
            <ServerErrorPage error={errorObj} />
        </AppProviders>
    );
};

const AppPage = () => {
    const appData = useAppData();

    return (
        <AppProviders>
            <AppLayout data={appData}>
                <Outlet />
            </AppLayout>
        </AppProviders>
    );
};

export default AppPage;
